/**
 * handlers/workflows.ts
 * Load, create, and delete scheduled workflows.
 */

import { state } from '../state';
import { dom } from '../dom';
import {
  getWorkflows as apiGetWorkflows,
  createWorkflow as apiCreateWorkflow,
  deleteWorkflow as apiDeleteWorkflow,
} from '../api';
import { renderWorkflowsList, showToast } from '../ui-renderers';
import { isClientUser } from './helpers';

/**
 * Load and render the user's scheduled workflows.
 * No-op for client users.
 */
export function loadWorkflows() {
  if (isClientUser()) return;
  const list = dom.wfList;
  if (!list) return;
  list.innerHTML = '<div class="ai-panel-loading">Loading workflows&hellip;</div>';

  apiGetWorkflows()
    .then(data => {
      state.workflows = data.workflows || [];
      renderWorkflowsList(state.workflows);
    })
    .catch(err => {
      console.warn('[AkiliBrain:workflows]', err);
      if (list) list.innerHTML =
        '<div class="ai-panel-empty">Could not load workflows. Check your connection.</div>';
    });
}

/**
 * Create a new scheduled workflow from the form values.
 */
export async function handleCreateWorkflow(payload: {
  name: string;
  workflow_type: string;
  cron_schedule: string;
  matter_id?: string;
  custom_prompt?: string;
}) {
  if (!payload.name?.trim()) {
    showToast('Please give the workflow a name.', 'error');
    return;
  }
  if (!payload.cron_schedule?.trim()) {
    showToast('Please enter a cron schedule (e.g. 0 9 * * 1).', 'error');
    return;
  }
  if (payload.workflow_type === 'custom_prompt' && !payload.custom_prompt?.trim()) {
    showToast('Please enter a custom prompt for this workflow.', 'error');
    return;
  }

  const saveBtn = dom.wfForm?.querySelector('#ai-wf-save-btn') as HTMLButtonElement | null;
  if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Creating…'; }

  try {
    await apiCreateWorkflow(payload);
    showToast('Workflow created!');
    document.dispatchEvent(new CustomEvent('akili:wf-form-cancel'));
    loadWorkflows();
  } catch (err: any) {
    showToast(err.message || 'Failed to create workflow.', 'error');
    console.warn('[AkiliBrain:workflows]', err);
  } finally {
    if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Create Workflow'; }
  }
}

/**
 * Delete a workflow by ID and reload the list.
 */
export async function handleDeleteWorkflow(id: string) {
  try {
    await apiDeleteWorkflow(id);
    state.workflows = state.workflows.filter((w: any) => w.id !== id);
    showToast('Workflow deleted.');
    renderWorkflowsList(state.workflows);
  } catch (err) {
    showToast('Could not delete workflow.', 'error');
    console.warn('[AkiliBrain:workflows]', err);
  }
}
